import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment';
import { FilterType } from '../grid/utils/filter/filter.utils';
import { isForwardCalendar } from '../helpers/calendar';
import { IssueDate } from '../helpers/issue/date';
import { CalendarFormat } from '../types/calendar.types';

interface BaseOption {
  value: Moment;
  label: string;
  checked: boolean;
}

export interface DayOption extends BaseOption {
  isDate: boolean;
}

export interface WeekOption extends BaseOption {
  includeLater?: boolean;
  days: DayOption[];
}

export interface DaysFilter extends DayOption {
  isDay: boolean;
  includeLater?: boolean;
}

export interface DateFilterVM {
  isAllDaysChecked: boolean;
  isCalendarWeek: boolean;
  weekStartingDate: string;
  weeks: WeekOption[];
}

interface DateFilterGridModelBase {
  values: string[];
  vm: DateFilterVM;
}

export interface DateFilterGridModel extends DateFilterGridModelBase {
  filterType: FilterType;
  isFilterActive: boolean;
}

@Injectable()
export class DateFilterService {
  public hasWeekChanged(
    selectedWeekDate: string,
    passedWeekDate: string
  ): boolean {
    return selectedWeekDate !== passedWeekDate;
  }

  public getFilterModel(
    date: string,
    isLiveBid: boolean = false,
    fromBidcomp?: boolean
  ): DateFilterVM {
    let weeks: WeekOption[];

    if (!(this.isForwardCalendar(date) || fromBidcomp)) {
      const days: DayOption[] = [
        ...this.getWeekDays(date, 'ddd MM/DD'),
        {
          value: undefined,
          label: IssueDate.Priority.DayToDay,
          isDate: false,
          checked: true
        }
      ];

      weeks = [
        {
          label: null,
          value: null,
          checked: true,
          days
        }
      ];
      if (isLiveBid) {
        days.push({
          value: undefined,
          label: IssueDate.Priority.TBD,
          isDate: false,
          checked: true
        });
      }
    } else {
      weeks = new Array(3)
        .fill(moment(date))
        .map((w, i) => moment(w).add(i, 'w'))
        .map<WeekOption>((w) => ({
          value: w,
          label: `Week of ${moment(w).format('MM/DD')}`,
          checked: true,
          days: this.getWeekDays(moment(w).format('YYYY-MM-DD'), 'dddd')
        }));

      if (fromBidcomp) {
        weeks.push({
          value: null,
          label: IssueDate.Priority.DayToDay,
          checked: true,
          days: []
        });
      } else {
        weeks.push({
          value: moment(weeks[weeks.length - 1].value).add(1, 'w'),
          label: `Week of ${moment(weeks[weeks.length - 1].value)
            .add(1, 'w')
            .format('MM/DD')} or later`,
          checked: true,
          includeLater: true,
          days: []
        });
      }

      weeks.push({
        value: null,
        label: IssueDate.Priority.TBD,
        checked: true,
        days: []
      });
    }

    return {
      isCalendarWeek: this.isCalendarWeek(weeks),
      isAllDaysChecked: this.isAllDaysChecked(weeks),
      weekStartingDate: date,
      weeks
    };
  }

  public isFilterActive(vm: DateFilterVM): boolean {
    return !!vm && vm.weeks
      ? toDaysFilters(vm.weeks).some((d) => !d.checked)
      : false;
  }

  public getFilters(vm: DateFilterVM): DateFilterGridModelBase {
    if (!vm || (vm && !Array.isArray(vm.weeks))) {
      return {
        values: [],
        vm: {
          isAllDaysChecked: true,
          isCalendarWeek: true,
          weekStartingDate: null,
          weeks: []
        }
      };
    }

    return {
      values: this.isCalendarWeek(vm.weeks)
        ? toCalendarFilters(vm.weeks)
        : toForwardCalendarFilters(vm.weeks),
      vm
    };
  }

  public getFilterString(weeks: WeekOption[]): string {
    const isCalendarWeek: boolean = this.isCalendarWeek(weeks);

    if (!Array.isArray(weeks)) {
      return '';
    } else if (!isCalendarWeek && weeks.every((w) => w.checked)) {
      return '';
    } else if (isCalendarWeek && weeks[0].days.every((d) => d.checked)) {
      return '';
    }

    return (isCalendarWeek
      ? toCalendarFilters(weeks)
      : toForwardCalendarFilters(weeks)
    ).join(', ');
  }

  public setGridModel(model: DateFilterGridModel): DateFilterVM {
    return {
      isAllDaysChecked: model.vm.weeks.every((w) => w.checked),
      isCalendarWeek: this.isCalendarWeek(model.vm.weeks),
      weekStartingDate: model.vm.weekStartingDate,
      weeks: model.vm.weeks
    };
  }

  public isInSelectedDateRange(
    weeks: WeekOption[],
    deal: { dealDate: Moment; isTBD: boolean; isDayToDay: boolean }
  ): boolean {
    if (weeks.every((w) => w.checked)) {
      return true;
    }

    return toDaysFilters(weeks)
      .filter((f) => !!f.checked)
      .some((f) => {
        if (!f.isDate) {
          return (
            (f.label === 'TBD' && deal.isTBD) ||
            (f.label === 'Day to Day' && deal.isDayToDay)
          );
        }

        if (!deal.dealDate) {
          return false;
        }

        const date: Moment = moment(deal.dealDate, 'ddd M/DD, YYYY').startOf(
          'day'
        );
        const option: Moment = moment(f.value, 'ddd M/DD, YYYY')
          .startOf('day')
          .utc();

        return f.includeLater
          ? date.isSameOrAfter(option, 'day')
          : date.isSame(option, 'day');
      });
  }

  public onChanged(
    vm: DateFilterVM,
    option: WeekOption | DayOption,
    checked: boolean
  ): DateFilterVM {
    const isCalendarWeek: boolean = this.isCalendarWeek(vm.weeks);
    let weeks: WeekOption[];

    if (!isCalendarWeek) {
      if ((option as WeekOption).days) {
        weeks = vm.weeks.map((w) =>
          w.label === option.label
            ? {
                ...w,
                checked,
                days:
                  w.days &&
                  w.days.map((d) => ({
                    ...d,
                    checked
                  }))
              }
            : w
        );
      } else {
        weeks = vm.weeks
          .map((w) => ({
            ...w,
            days:
              w.days &&
              w.days.map((d) => ({
                ...d,
                checked: moment(d.value).isSame(option.value, 'date')
                  ? checked
                  : d.checked
              }))
          }))
          .map((w) => ({
            ...w,
            checked:
              w.days && w.days.length > 0
                ? w.days.every((d) => d.checked)
                : w.checked
          }));
      }
    } else {
      weeks = vm.weeks
        .map((w) => ({
          ...w,
          days: w.days.map((d) => ({
            ...d,
            checked:
              d.label === (option as DayOption).label ? checked : d.checked
          }))
        }))
        .map((w) => ({
          ...w,
          checked: w.days.every((d) => d.checked)
        }));
    }

    return {
      ...vm,
      weeks,
      isAllDaysChecked: this.isAllDaysChecked(weeks)
    };
  }

  private isForwardCalendar(date: string): boolean {
    return isForwardCalendar(moment(date, CalendarFormat.ISO, true));
  }

  private getWeekDays(date: string, format: string): DayOption[] {
    const opts: DayOption[] = [];

    for (let i = 0; i <= 4; i++) {
      const day = moment(date).isoWeekday(1).startOf('day').add(i, 'd');

      opts.push({
        value: day,
        label: moment(day).format(format),
        isDate: true,
        checked: true
      });
    }

    return opts;
  }

  private isAllDaysChecked(weeks: WeekOption[]): boolean {
    const isCalendarWeek: boolean = this.isCalendarWeek(weeks);

    if (isCalendarWeek) {
      const [{ days }] = weeks;

      return !!(days && days.every((d) => d.checked));
    } else {
      return weeks.every((w) => w.checked);
    }
  }

  private isCalendarWeek(weeks: WeekOption[]): boolean {
    if (Array.isArray(weeks) && weeks.length === 1) {
      const [week] = weeks;

      if (week && !(week.label && week.value)) {
        return true;
      }
    }

    return false;
  }
}

function toDaysFilters(weeks: WeekOption[]): DaysFilter[] {
  if (!Array.isArray(weeks)) {
    return [];
  }

  return weeks.reduce(
    (prev, week: WeekOption) =>
      [...prev].concat(
        week.days && week.days.length > 0
          ? week.days.map(
              ({ value, label, checked, isDate }) =>
                ({
                  value,
                  label,
                  checked,
                  isDate,
                  isDay: true,
                  includeLater: undefined
                } as DaysFilter)
            )
          : {
              value: week.value,
              label: week.label,
              checked: week.checked,
              isDate: !!week.value,
              isDay: false,
              includeLater: week.includeLater
            }
      ),
    [] as DaysFilter[]
  );
}

function toForwardCalendarFilters(weeks: WeekOption[]): string[] {
  return weeks.reduce(
    (prev: string[], week: WeekOption) =>
      week && week.days && week.days.length > 0
        ? [
            ...prev,
            week.days.some((d) => d.checked)
              ? `${week.label} (${week.days
                  .filter((day) => day.checked)
                  .map((day) => day.label.slice(0, 3))
                  .join(', ')})`
              : ''
          ].filter((day) => !!day)
        : !!week.checked && week.days && week.days.length === 0
        ? [...prev, week.label]
        : [...prev],
    [] as string[]
  );
}

function toCalendarFilters(weeks: WeekOption[]): string[] {
  return weeks.reduce(
    (prev: string[], week: WeekOption) => [
      ...prev,
      ...week.days.filter((day) => day.checked).map((day) => day.label)
    ],
    [] as string[]
  );
}
